import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Clock,
  Calendar, CheckCircle, XCircle,
  X,
  ArrowLeft
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);


const OrderStatus = ({ status, message, handleClose }) => {
  return (
    <div className="p-6 space-y-4 text-center">
      {status === 'success' ? (
        <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
      ) : (
        <XCircle className="w-16 h-16 mx-auto text-amber-500" />
      )}
      <h3 className={`text-xl font-semibold ${status === 'success' ? 'text-green-700' : 'text-amber-700'}`}>
        {status === 'success' ? 'Order Placed Successfully!' : 'Payment Not Completed'}
      </h3>
      <p className="text-gray-600">
        {status === 'success' ? 'Please check your email for order status and updates.' : message}
      </p>
      <button
        onClick={handleClose}
        className="px-6 py-3 font-medium text-white transition-colors shadow-lg bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl hover:from-amber-700 hover:to-amber-800"
      >
        {status === 'success' ? 'Close' : 'Try Again'}
      </button>
    </div>
  );
};

// PaymentForm component
const PaymentForm = ({ formData, orderItems, gstTax, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add this function inside PaymentForm
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      if (paymentIntent.status !== 'succeeded') {
        throw new Error('Payment not completed successfully');
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/stripe/payment-success`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_intent: paymentIntent.id,
          order_details: {
            items: orderItems,
            gstTax,
            customerDetails: formData,
            totalAmount: paymentIntent.amount / 100
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess(data);
      } else {
        throw new Error(data.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      throw new Error('Failed to verify payment');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment intent
      const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderItems,
          gstTax,
          customerDetails: formData
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret } = await response.json();

      // Confirm payment
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          },
        },
        receipt_email: formData.email,
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      await handlePaymentSuccess(result.paymentIntent);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred while processing your payment.');
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4 space-y-4 border rounded-lg border-amber-200">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-amber-900">
            Card Details
          </label>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#713f12',
                  '::placeholder': {
                    color: '#b45309',
                  },
                },
              },
            }}
            className="p-3 border rounded-lg border-amber-200"
          />
        </div>
        {error && (
          <div className="p-3 text-sm text-red-600 rounded-lg bg-red-50">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !stripe}
          className="w-full px-6 py-3 font-medium text-white transition-colors shadow-lg bg-gradient-to-r from-amber-600 to-amber-700 rounded-xl hover:from-amber-700 hover:to-amber-800 disabled:opacity-50"
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </button>
      </div>
    </form>
  );
};

const MobileCheckoutForm = ({
  isOpen,
  onClose,
  orderItems,
  restaurantInfo = {
    name: "Mr. WoodFire",
    address: "55-61 Railway avenue Werribee Victoria"
  }
}) => {
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const calculateSubtotal = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const gstTax = calculateSubtotal() * 0.10;
  const total = calculateSubtotal() + gstTax;
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = async () => {
    if (step === 1) {
      try {
        setIsVerifying(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/auth/send-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            name: formData.name
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send OTP');
        }
        setCooldown(120);
        setResendDisabled(true);
        setStep(2);
      } catch (error) {
        console.error('Error sending OTP:', error);
        alert('Failed to send verification code. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    } else if (step === 2) {
      try {
        setIsVerifying(true);
        const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/auth/verify-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            otp: otp
          }),
        });

        if (!response.ok) {
          throw new Error('Invalid OTP');
        }

        setOtpError('');
        setStep(3);
      } catch (error) {
        setOtpError('Invalid verification code. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    } else {
      setStep(prev => prev + 1);
    }
  };

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldown]);

  const handleResendOTP = async () => {
    try {
      setIsVerifying(true);
      setResendDisabled(true);

      const response = await fetch(`${process.env.REACT_APP_API_BASEURL}/api/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to resend OTP');
      }

      // Start the cooldown timer (120 seconds = 2 minutes)
      setCooldown(120);

    } catch (error) {
      console.error('Error resending OTP:', error);
      setResendDisabled(false);
    } finally {
      setIsVerifying(false);
    }
  };


  const handleBack = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep(prev => prev - 1);
    }
  };
  const handlePaymentSuccess = (data) => {
    setOrderStatus('success');
    setStatusMessage(data.message || 'Your order has been placed successfully!');
  };

  const handlePaymentError = (message) => {
    setOrderStatus('error');
    setStatusMessage(message || 'There was an error processing your payment.');
  };

  const handleModalClose = () => {
    if (orderStatus === 'success') {
      setStep(1);
      setFormData({
        name: '',
        email: '',
        phone: '',
      });
      setOrderStatus(null);
      setStatusMessage('');
      onClose();
    } else {
      setOrderStatus(null);
      setStatusMessage('');
    }
  };

  // Add this function inside MobileCheckoutForm component
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.name.trim() !== '' &&
          formData.email.trim() !== '' &&
          formData.phone.trim() !== '' &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email); // Basic email validation
      case 2:
        return otp.length === 6; // Validate OTP length
      case 3:
        return true; // Order summary doesn't need validation
      case 4:
        return true; // Stripe handles its own validation
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] md:hidden">
      {/* Overlay with fade in/out */}
      <div className="absolute inset-0 transition-opacity duration-300 bg-black/60 backdrop-blur-sm" />

      {/* Modal content with slide up animation */}
      <div className="absolute inset-x-0 bottom-0 animate-slide-up">
        <div className="flex flex-col h-[90vh] bg-white rounded-t-2xl">
          {/* Header */}
          <div className="flex items-center gap-4 p-4 border-b border-amber-100">
            <button
              onClick={handleBack}
              className="p-2 transition-colors rounded-full text-amber-500 hover:bg-amber-50"
            >
              {step === 1 ? <X className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
            </button>
            <h2 className="text-xl font-bold text-amber-900">Checkout</h2>
            <div className="ml-auto text-sm text-amber-600">
              Step {step} of 3
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex gap-2 px-6 py-3 bg-gradient-to-r from-amber-50 to-orange-50">
            <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-amber-500' : 'bg-amber-100'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-amber-500' : 'bg-amber-100'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 3 ? 'bg-amber-500' : 'bg-amber-100'}`} />
            <div className={`h-1 flex-1 rounded-full ${step >= 4 ? 'bg-amber-500' : 'bg-amber-100'}`} />
          </div>



          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {orderStatus ? (
                <OrderStatus
                  status={orderStatus}
                  message={statusMessage}
                  handleClose={handleModalClose}
                />
              ) : (
                <>
                  {step === 1 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-amber-900">Personal Details</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block mb-2 text-sm font-medium text-amber-900">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute w-5 h-5 text-amber-500 left-3 top-3" />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500
      ${formData.name.trim() === '' ? 'border-red-200' : 'border-amber-200'}`}
                              placeholder="Enter your full name"
                              required
                            />
                          </div>

                        </div>
                        <div>
                          <label className="block mb-2 text-sm font-medium text-amber-900">
                            Email Address
                          </label>
                          <div className="relative">
                            <Mail className="absolute w-5 h-5 text-amber-500 left-3 top-3" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              placeholder="Enter your email address"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block mb-2 text-sm font-medium text-amber-900">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone className="absolute w-5 h-5 text-amber-500 left-3 top-3" />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full pl-10 pr-4 py-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              placeholder="Enter your phone number"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {step === 2 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-amber-900">Email Verification</h3>
                      <p className="text-amber-600">We've sent a verification code to {formData.email}</p>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-amber-900">
                          Enter Verification Code <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          className="w-full px-4 py-2.5 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                        />
                        {otpError && (
                          <p className="mt-2 text-sm text-red-600">{otpError}</p>
                        )}
                      </div>
                      <button
                        onClick={handleResendOTP}
                        disabled={isVerifying || resendDisabled}
                        className="flex items-center text-sm font-medium text-amber-600 hover:text-amber-700 disabled:opacity-50"
                      >
                        {isVerifying ? (
                          <>
                            <span className="w-4 h-4 mr-2 border-2 rounded-full border-amber-600 border-t-transparent animate-spin" />
                            Sending Code...
                          </>
                        ) : cooldown > 0 ? (
                          `Resend code in ${Math.floor(cooldown / 60)}:${(cooldown % 60).toString().padStart(2, '0')}`
                        ) : (
                          'Resend Code'
                        )}
                      </button>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-amber-900">Order Summary</h3>
                      <div className="p-4 border rounded-xl border-amber-100 bg-amber-50/50">
                        {/* Restaurant Info */}
                        <div className="flex items-start gap-3 mb-4">
                          <Building2 className="flex-shrink-0 w-5 h-5 mt-1 text-amber-500" />
                          <div>
                            <h3 className="font-medium text-amber-900">{restaurantInfo.name}</h3>
                            <div className="flex items-center mt-1 text-sm text-amber-600">
                              <MapPin className="w-4 h-4 mr-1" />
                              {restaurantInfo.address}
                            </div>
                          </div>
                        </div>

                        {/* Pickup Time */}
                        <div className="flex items-center gap-3 mb-4">
                          <Clock className="flex-shrink-0 w-5 h-5 text-amber-500" />
                          <div>
                            <div className="flex items-center text-sm text-amber-600">
                              <Calendar className="w-4 h-4 mr-1" />
                              Today
                            </div>
                            <p className="font-medium text-amber-900">Ready in 15-20 mins</p>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-3">
                          {orderItems.map((item) => (
                            <div key={item.id} className="flex justify-between text-sm">
                              <span className="text-amber-700">{item.quantity}x {item.label}</span>
                              <span className="font-medium text-amber-900">${(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        <div className="h-px my-4 bg-amber-200" />

                        {/* Totals */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-amber-600">Subtotal</span>
                            <span className="text-amber-900">${calculateSubtotal().toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-amber-600">GST (10%)</span>
                            <span className="text-amber-900">${gstTax.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between pt-2 font-medium">
                            <span className="text-amber-900">Total</span>
                            <span className="text-amber-900">${total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <Elements stripe={stripePromise}>
                      <PaymentForm
                        formData={formData}
                        orderItems={orderItems}
                        gstTax={gstTax}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                      />
                    </Elements>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          {!orderStatus && (
            <div className="p-6 border-t border-amber-100">
              <button
                onClick={step === 4 ? null : handleNext}
                disabled={!isStepValid() || isVerifying}
                className={`flex items-center justify-center w-full px-6 py-3 font-medium text-white transition-colors shadow-lg rounded-xl
    ${isStepValid() && !isVerifying
                    ? 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800'
                    : 'bg-gray-300 cursor-not-allowed'}`}
              >
                {isVerifying ? (
                  <>
                    <span className="w-5 h-5 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin" />
                    {step === 1 ? 'Sending Code...' : 'Verifying...'}
                  </>
                ) : (
                  <>
                    {step === 4 ? 'Pay Now' : 'Continue'}
                    {step !== 4 && <ChevronRight className="w-5 h-5 ml-2" />}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileCheckoutForm;